// src/pages/Home/sections/UnsubscribePage.jsx
import React, { useEffect, useState } from "react";
import { XCircle, Loader2, BellOff } from "lucide-react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API_ENDPOINTS } from "../../../api";

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error" | "missing"
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("missing");
      return;
    }

    const unsubscribe = async () => {
      try {
        const data = await api.post(
          `${API_ENDPOINTS.NEWSLETTER_UNSUBSCRIBE}?token=${encodeURIComponent(token)}`
        );

        if (data.success) {
          setEmail(data.email);
          setStatus("success");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    };

    unsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card w-full max-w-md p-8 text-center">

        <div className="flex justify-center mb-6">
          {status === "loading" && (
            <Loader2 className="w-16 h-16 text-primary animate-spin" />
          )}
          {status === "success" && (
            <div className="bg-surface-border rounded-full p-4">
              <BellOff className="w-12 h-12 text-primary" />
            </div>
          )}
          {(status === "error" || status === "missing") && (
            <div className="bg-red-500/10 rounded-full p-4">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
          )}
        </div>

        {status === "loading" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-2">
              Processing your request…
            </h1>
            <p className="font-body text-text-muted text-sm">
              Please wait a moment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-3">
              You've been unsubscribed
            </h1>
            {email && (
              <p className="font-body text-text-muted text-sm font-semibold mb-2">
                {email}
              </p>
            )}
            <p className="font-body text-text-muted text-sm mb-6">
              You'll no longer receive newsletter emails from RentGurus. If
              this was not you, you can re-subscribe at any time from our
              homepage.
            </p>
            <Link
              to="/"
              className="inline-block w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold text-sm uppercase tracking-wider py-4 rounded-xl transition-colors"
            >
              Back to homepage
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-3">
              Something went wrong
            </h1>
            <p className="font-body text-text-muted text-sm mb-6">
              This unsubscription link is invalid or has already been used.
              If you're still receiving emails, please contact us.
            </p>
            <Link
              to="/"
              className="inline-block w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold text-sm uppercase tracking-wider py-4 rounded-xl transition-colors"
            >
              Back to homepage
            </Link>
          </>
        )}

        {status === "missing" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-3">
              Invalid link
            </h1>
            <p className="font-body text-text-muted text-sm mb-6">
              No unsubscription token was found. Please use the exact link
              from the email you received.
            </p>
            <Link
              to="/"
              className="inline-block w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold text-sm uppercase tracking-wider py-4 rounded-xl transition-colors"
            >
              Back to homepage
            </Link>
          </>
        )}

        {status !== "loading" && (
          <p className="font-body text-text-muted text-xs mt-6">
            Need help?{" "}
            <Link to="/support" className="text-primary hover:underline">
              Contact us
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
